import React, { useMemo, useState, useCallback } from 'react';
import { Rect } from 'react-konva';
import { addDays, differenceInDays } from 'date-fns';
import { KonvaEventObject } from 'konva/lib/Node';
import Konva from 'konva';

interface BarResizerProps {
  startDate: Date;
  endDate: Date;
  schedulerStartDate: Date;
  dayWidth: number;
  trackHeight: number;
  onResizeComplete: (updatedStartDate: Date, updatedEndDate: Date) => void;
  verticalOffset?: number; // New prop for vertical positioning
}

export const BarResizer: React.FC<BarResizerProps> = React.memo(function BarResizer({ 
  startDate, 
  endDate, 
  schedulerStartDate, 
  dayWidth, 
  trackHeight, 
  onResizeComplete,
  verticalOffset = 0 // Default to 0 if not provided
}: BarResizerProps) {
  const [currentStartDate, setCurrentStartDate] = useState(startDate);
  const [currentEndDate, setCurrentEndDate] = useState(endDate);
  const [isDragging, setIsDragging] = useState(false);

  // Memoize calculations to reduce re-renders
  const startDaysFromStart = useMemo(() => 
    differenceInDays(currentStartDate, schedulerStartDate),
    [currentStartDate, schedulerStartDate]
  );
  
  const barWidth = useMemo(() => 
    (differenceInDays(currentEndDate, currentStartDate) + 1) * dayWidth,
    [currentStartDate, currentEndDate, dayWidth]
  );

  const startX = useMemo(() => startDaysFromStart * dayWidth, 
    [startDaysFromStart, dayWidth]);
    
  const endX = useMemo(() => (startDaysFromStart + differenceInDays(currentEndDate, currentStartDate) + 1) * dayWidth, 
    [startDaysFromStart, currentEndDate, currentStartDate, dayWidth]);

  const handleDragMove = useCallback((newX: number, isStart: boolean) => {
    const previousX = isStart ? startX : endX;
    const daysMoved = Math.round((newX - previousX) / dayWidth);
    const newDate = isStart ? addDays(currentStartDate, daysMoved) : addDays(currentEndDate, daysMoved);

    if (isStart) {
      if (newDate.getDate() !== currentStartDate.getDate()) {
        setCurrentStartDate(newDate);
      }
    } else {
      if (newDate.getDate() !== currentEndDate.getDate()) {
        setCurrentEndDate(newDate);
      }
    }
  }, [startX, endX, dayWidth, currentStartDate, currentEndDate]);

  const handleBarDragMove = useCallback((newX: number) => {
    const daysMoved = Math.round(newX / dayWidth) - startDaysFromStart;
    const newStartDate = addDays(currentStartDate, daysMoved);
    const newEndDate = addDays(currentEndDate, daysMoved);

    if (newStartDate.getDate() !== currentStartDate.getDate() || newEndDate.getDate() !== currentEndDate.getDate()) {
      setCurrentStartDate(newStartDate);
      setCurrentEndDate(newEndDate);
    }
  }, [dayWidth, startDaysFromStart, currentStartDate, currentEndDate]);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    onResizeComplete(currentStartDate, currentEndDate);
  }, [currentStartDate, currentEndDate, onResizeComplete]);

  // Memoize drag bound functions
  const dragBoundFunc = useCallback((pos: { x: number; y: number }, isStart: boolean) => {
    const oppositeX = isStart ? endX : startX;
    const minX = isStart ? 0 : oppositeX + dayWidth;
    const maxX = isStart ? oppositeX - dayWidth : 365 * dayWidth;
    return {
      x: Math.min(Math.max(Math.round(pos.x / dayWidth) * dayWidth, minX), maxX),
      y: 5 + verticalOffset, // Apply vertical offset
    };
  }, [endX, startX, dayWidth, verticalOffset]);

  const dragBarBoundFunc = useCallback((pos: { x: number; y: number }) => {
    const maxX = 365 * dayWidth - barWidth;
    const boundedX = Math.min(Math.max(Math.round(pos.x / dayWidth) * dayWidth, 0), maxX);
    return { x: boundedX, y: 5 + verticalOffset }; // Apply vertical offset
  }, [dayWidth, barWidth, verticalOffset]);

  // Event handlers for mouse enter/leave
  const handleMouseEnter = useCallback((e: KonvaEventObject<MouseEvent>, cursor: string) => {
    const stage = e.target.getStage();
    if (stage) stage.container().style.cursor = cursor;
    
    if (e.target instanceof Konva.Shape && cursor === 'ew-resize') {
      e.target.fill('darkblue');
    }
  }, []);

  const handleMouseLeave = useCallback((e: KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    if (stage) stage.container().style.cursor = 'default';
    
    if (e.target instanceof Konva.Shape) {
      e.target.fill('transparent');
    }
  }, []);

  return (
    <>
      {/* Resizer bar */}
      <Rect
        x={startDaysFromStart * dayWidth}
        y={5 + verticalOffset} // Apply vertical offset
        width={barWidth}
        height={trackHeight - 10}
        fill={isDragging ? "rgba(0, 0, 0, 0.2)" : "transparent"}
        cornerRadius={4}
        draggable
        dragBoundFunc={dragBarBoundFunc}
        onDragStart={handleDragStart}
        onDragMove={(e) => handleBarDragMove(e.target.x())}
        onDragEnd={handleDragEnd}
        onMouseEnter={(e) => handleMouseEnter(e, 'grab')}
        onMouseLeave={handleMouseLeave}
      />
      {/* Left resize handle */}
      <Rect
        x={startDaysFromStart * dayWidth}
        y={5 + verticalOffset} // Apply vertical offset
        width={10}
        height={trackHeight - 10}
        fill="transparent"
        draggable
        dragBoundFunc={(pos) => dragBoundFunc(pos, true)}
        onDragStart={handleDragStart}
        onDragMove={(e) => handleDragMove(e.target.x(), true)}
        onDragEnd={handleDragEnd}
        onMouseEnter={(e) => handleMouseEnter(e, 'ew-resize')}
        onMouseLeave={handleMouseLeave}
      />
      {/* Right resize handle */}
      <Rect
        x={(startDaysFromStart + differenceInDays(currentEndDate, currentStartDate) + 1) * dayWidth - 10}
        y={5 + verticalOffset} // Apply vertical offset
        width={10}
        height={trackHeight - 10}
        fill="transparent"
        draggable
        dragBoundFunc={(pos) => dragBoundFunc(pos, false)}
        onDragStart={handleDragStart}
        onDragMove={(e) => handleDragMove(e.target.x(), false)}
        onDragEnd={handleDragEnd}
        onMouseEnter={(e) => handleMouseEnter(e, 'ew-resize')}
        onMouseLeave={handleMouseLeave}
      />
    </>
  );
});
